import { useState } from 'react';
import type { HydratedAsset } from '../publicDto';
import { AssetMedia } from './AssetMedia';

// Ad Preview Mockups Stage 1 §4.5. A single Meta Feed ad mockup.
//
// Structure (top to bottom):
//   - Header: brand profile pic + name + "Sponsored"
//   - Primary text (body copy) above the asset, truncated to 3 lines
//     with a "...more" affordance that expands the rest
//   - Asset slot at 4:5
//   - Headline (bold) + description below the asset
//   - CTA button to the right of the headline/description
//   - Footer: Like / Comment / Share row (decorative)
//
// The body copy / headlines / descriptions arrays often hold multiple
// variants per Campaign (Meta rotates between them). For the preview
// we render the FIRST entry of each — same convention as Build View's
// resolved preview. Future improvement (Stage 4) could cycle variants.

type Props = {
  asset: HydratedAsset;
  // Brand identity — drives the BrandCircle's initial char only.
  // Falls back as the chrome page-name text if `pageName` isn't passed,
  // so older callers keep working.
  brandName: string;
  // Stage 1 §13.16. Simulated Meta page name rendered in the chrome
  // next to the BrandCircle (e.g. "Snap Fitness Barrington" composed
  // from clubNamePrefix + franchiseSuburb). Distinct from brandName
  // because franchisees recognise the location, not just the brand.
  // Optional for older callers / tests; defaults to brandName.
  pageName?: string;
  bodyCopy: string | null;
  headline: string | null;
  description: string | null;
  ctaLabel: string | null;
};

export function FeedMockup({
  asset,
  brandName,
  pageName,
  bodyCopy,
  headline,
  description,
  ctaLabel,
}: Props) {
  // Chrome page label resolves to the composed location name when
  // present; falls back to brandName so test renders without pageName
  // still show something sensible.
  const chromeName = pageName?.trim() || brandName;
  return (
    <div className="mx-auto max-w-[500px] w-full">
      <article className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <header className="flex items-center gap-3 px-4 py-3">
          <BrandCircle brandName={brandName} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">
              {chromeName}
            </p>
            <p className="text-xs text-slate-500">Sponsored</p>
          </div>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="text-slate-400 cursor-default"
            title="More options (decorative)"
          >
            ⋯
          </button>
        </header>

        {/* Body copy — primary text above the asset. Meta truncates
            primary text at 3 lines + "...more" before expanding. */}
        {bodyCopy && bodyCopy.trim().length > 0 && (
          <BodyCopy text={bodyCopy} />
        )}

        {/* Asset slot — image or video at 4:5. */}
        <AssetMedia asset={asset} aspectClass="aspect-[4/5]" />

        {/* Headline + description + CTA row */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex items-start gap-3">
          <div className="flex-1 min-w-0 space-y-0.5">
            {/* Display URL — show the brand domain stripped to a
                "sponsored host" feel. Meta surfaces this as a small
                uppercase label above the headline. */}
            <p className="text-[10px] uppercase tracking-wide text-slate-500">
              Sponsored
            </p>
            {headline && (
              <p className="text-sm font-semibold text-slate-900 leading-snug">
                {headline}
              </p>
            )}
            {description && (
              <p className="text-xs text-slate-600 line-clamp-2">
                {description}
              </p>
            )}
          </div>
          {ctaLabel && (
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              className="shrink-0 rounded-md bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-800 cursor-default hover:bg-slate-300"
            >
              {ctaLabel}
            </button>
          )}
        </div>

        {/* Footer — decorative engagement row */}
        <footer className="flex items-center justify-around border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          <span aria-hidden>👍 Like</span>
          <span aria-hidden>💬 Comment</span>
          <span aria-hidden>↗ Share</span>
        </footer>
      </article>
      {asset.label && (
        <p className="text-xs text-slate-500 text-center mt-2">{asset.label}</p>
      )}
    </div>
  );
}

// Body copy truncated to 3 lines with a "more" expander. We use a
// simple state toggle rather than measuring text height — Meta's
// behaviour is purely visual ("show more lines"), and the truncation
// itself comes from a CSS line-clamp utility.
function BodyCopy({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="px-4 py-3 text-sm text-slate-800">
      <p
        className={expanded ? 'whitespace-pre-wrap' : 'whitespace-pre-wrap line-clamp-3'}
      >
        {text}
      </p>
      {!expanded && text.length > 160 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-xs text-slate-500 hover:text-slate-900"
        >
          …more
        </button>
      )}
    </div>
  );
}

function BrandCircle({ brandName }: { brandName: string }) {
  const initial = brandName.trim().charAt(0).toUpperCase() || '•';
  return (
    <div className="h-10 w-10 rounded-full bg-slate-300 text-slate-900 flex items-center justify-center text-sm font-semibold">
      {initial}
    </div>
  );
}
